import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { humanizeString } from "@/lib/utils";
import { ActivityEvent } from "@/types";
import {
  Activity,
  Clock,
  Filter,
  GitBranch,
  Settings,
  Users,
  Zap,
} from "lucide-react";
import { useState } from "react";

interface ActivityFeedProps {
  showFilters?: boolean;
  activities: ActivityEvent[];
}

export function ActivityFeed({
  activities,
  showFilters = true,
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<string>("all");

  const filteredActivities = activities;

  const getActivityIcon = (type: string, action: string) => {
    // TODO align with backend
    switch (type) {
      case "deploy":
        return action === "failed" ? (
          <GitBranch className="h-4 w-4 text-red-400" />
        ) : (
          <GitBranch className="h-4 w-4 text-green-400" />
        );
      case "scaling":
        return <Zap className="h-4 w-4 text-yellow-400" />;
      case "domain":
        return <Settings className="h-4 w-4 text-blue-400" />;
      case "team":
        return <Users className="h-4 w-4 text-purple-400" />;
      default:
        return <Activity className="h-4 w-4 text-slate-400" />;
    }
  };

  const getActivityBadge = (type: string, action: string) => {
    const colors = {
      // Generic CRUD Actions
      create: "text-green-400 bg-green-400/10 border-green-400/30",
      update: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      delete: "text-red-400 bg-red-400/10 border-red-400/30",
      read: "text-slate-400 bg-slate-400/10 border-slate-400/30",

      // Application Lifecycle Actions
      deploy:
        action === "failed"
          ? "text-red-400 bg-red-400/10 border-red-400/30"
          : "text-green-400 bg-green-400/10 border-green-400/30",
      rollback: "text-orange-400 bg-orange-400/10 border-orange-400/30",
      start: "text-green-400 bg-green-400/10 border-green-400/30",
      stop: "text-red-400 bg-red-400/10 border-red-400/30",
      restart: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
      scale: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
      scaling: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30", // keeping your original
      build: "text-indigo-400 bg-indigo-400/10 border-indigo-400/30",
      release: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
      add_domain: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      remove_domain: "text-red-400 bg-red-400/10 border-red-400/30",

      // Configuration Actions
      set_var: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",
      unset_var: "text-orange-400 bg-orange-400/10 border-orange-400/30",
      update_config: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",

      // Add-on Actions
      provision_addon: "text-green-400 bg-green-400/10 border-green-400/30",
      deprovision_addon: "text-red-400 bg-red-400/10 border-red-400/30",
      attach_addon: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      detach_addon: "text-orange-400 bg-orange-400/10 border-orange-400/30",
      upgrade_addon: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",

      // User/Authentication Actions
      login: "text-green-400 bg-green-400/10 border-green-400/30",
      logout: "text-gray-400 bg-gray-400/10 border-gray-400/30",
      password_reset_request:
        "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
      password_change: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      invite_user: "text-green-400 bg-green-400/10 border-green-400/30",
      remove_user: "text-red-400 bg-red-400/10 border-red-400/30",
      update_role: "text-purple-400 bg-purple-400/10 border-purple-400/30",

      // Team/Organization Actions
      create_team: "text-purple-400 bg-purple-400/10 border-purple-400/30",
      delete_team: "text-red-400 bg-red-400/10 border-red-400/30",
      join_team: "text-green-400 bg-green-400/10 border-green-400/30",
      leave_team: "text-orange-400 bg-orange-400/10 border-orange-400/30",
      team: "text-purple-400 bg-purple-400/10 border-purple-400/30", // keeping your original

      // Billing Actions
      update_billing_info: "text-amber-400 bg-amber-400/10 border-amber-400/30",
      add_payment_method: "text-green-400 bg-green-400/10 border-green-400/30",
      remove_payment_method: "text-red-400 bg-red-400/10 border-red-400/30",

      // System/Internal Actions
      system_maintenance:
        "text-orange-400 bg-orange-400/10 border-orange-400/30",
      health_check: "text-green-400 bg-green-400/10 border-green-400/30",
      backup: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      restore: "text-indigo-400 bg-indigo-400/10 border-indigo-400/30",

      // Error/Failure Actions
      failed: "text-red-400 bg-red-400/10 border-red-400/30",

      // Generic categories (keeping your originals)
      domain: "text-blue-400 bg-blue-400/10 border-blue-400/30",
      settings: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30",
    };

    return (
      <Badge className={`${colors[type as keyof typeof colors]}  text-xs`}>
        {humanizeString(type)}
      </Badge>
    );
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor(
      (now.getTime() - time.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <Card className="holo-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 ">
              <Activity className="h-5 w-5" />
              Activity Feed
            </CardTitle>
          </div>
          {showFilters && (
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-32 bg-slate-800/50 border-cyan-500/30 text-cyan-100 ">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-slate-300 ">
                    All
                  </SelectItem>
                  <SelectItem value="deploy" className="text-slate-300 ">
                    Applications
                  </SelectItem>
                  <SelectItem value="scaling" className="text-slate-300 ">
                    Scaling
                  </SelectItem>
                  <SelectItem value="domain" className="text-slate-300 ">
                    Domains
                  </SelectItem>
                  <SelectItem value="team" className="text-slate-300 ">
                    Team
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredActivities.map((activity, index) => (
            <div key={activity.id} className="relative">
              {/* Timeline line */}
              {index < filteredActivities.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-16 bg-slate-700"></div>
              )}

              <div className="flex gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center">
                  {getActivityIcon(activity.eventType, activity.action)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    {getActivityBadge(activity.resourceType, activity.action)}
                    <span className="text-xs text-slate-400  flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatTimeAgo(activity.timestamp)}
                    </span>
                  </div>

                  <p className="text-sm   mb-1">{activity.description}</p>

                  <p className="text-xs text-slate-400 ">
                    by {activity.userName}
                    {activity.resourceId && (
                      <span className="ml-2 px-2 py-1 bg-slate-800/50 rounded text-cyan-300">
                        {activity.resourceName}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {filteredActivities.length === 0 && (
            <div className="text-center py-8 text-slate-400 ">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              No activity found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
